'use client';
import React, { useState, useRef, useEffect, useCallback, Suspense } from 'react';
import classNames from 'classnames';
import { CloudinaryType, TypeBlockTabbedContent, TypeTabbedContentItem } from '@fm-nx/contentful-types';
import { useContentfulLiveUpdates, useContentfulInspectorMode } from '@contentful/live-preview/react';
import { CldImage } from 'next-cloudinary';
import TextMarkdown from '../text-markdown/text-markdown';

const MIN_TABS = 2;
const MAX_TABS = 5;

type TabbedContentPropTypes = {
    blockTabbedContent: TypeBlockTabbedContent<'WITHOUT_UNRESOLVABLE_LINKS'>;
    numberOfTabs?: number;
    mode?: 'dark' | 'light';
};

export const TabbedContent = (props: TabbedContentPropTypes) => {
    const { blockTabbedContent, numberOfTabs, mode = 'light' } = props;
    const updatedBlock = useContentfulLiveUpdates(blockTabbedContent);
    const inspectorProps = useContentfulInspectorMode({ entryId: updatedBlock.sys.id });
    const { fields } = updatedBlock;
    const [activeTab, setActiveTab] = useState(0);
    const isDark = mode === 'dark';
    const scrollRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const updateScrollState = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        setCanScrollLeft(el.scrollLeft > 0);
        setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        updateScrollState();
        const observer = new ResizeObserver(updateScrollState);
        observer.observe(el);
        return () => observer.disconnect();
    }, [updateScrollState]);

    if (!fields) return null;

    const { overline, headerText, paragraph, tabs } = fields;

    if (!tabs || tabs.length === 0) return null;

    const allValidTabs = tabs.filter((tab) => tab && tab.fields);

    if (allValidTabs.length < MIN_TABS) return null;

    const tabCount = numberOfTabs ? Math.max(MIN_TABS, Math.min(MAX_TABS, numberOfTabs)) : undefined;
    const validTabs = tabCount ? allValidTabs.slice(0, tabCount) : allValidTabs;

    const activeTabItem = validTabs[activeTab];

    return (
        <section>
            <div className="w-full mx-auto max-w-container">
                <div className="mx-6 py-14 md:mx-0">
                    <div className="flex flex-col md:grid md:grid-cols-14 md:gap-4">
                        <div className="col-span-14 md:col-span-12 md:col-start-2">
                            <div className="flex gap-6 flex-col">
                                {overline && (
                                    <h2
                                        className={classNames('h5 overline', { 'text-white': isDark })}
                                        {...inspectorProps({ fieldId: 'overline' })}
                                    >
                                        {overline}
                                    </h2>
                                )}
                                {headerText && (
                                    <h3
                                        className={classNames('h-xl', { 'text-white': isDark })}
                                        {...inspectorProps({ fieldId: 'headerText' })}
                                    >
                                        {headerText}
                                    </h3>
                                )}
                                {paragraph && (
                                    <TextMarkdown
                                        className={classNames('text-xl', { 'text-white': isDark })}
                                        text={paragraph}
                                        mode={mode}
                                        {...inspectorProps({ fieldId: 'paragraph' })}
                                    />
                                )}
                            </div>

                            <div className="relative mt-10">
                                <div
                                    ref={scrollRef}
                                    onScroll={updateScrollState}
                                    className={classNames('overflow-x-auto border-b', {
                                        'border-gray-500': isDark,
                                        'border-gray-300': !isDark,
                                    })}
                                    role="tablist"
                                    aria-label="Content tabs"
                                >
                                    <div className="flex gap-4 min-w-max">
                                        {validTabs.map((tab, index) => {
                                            if (!tab?.fields) return null;
                                            const isActive = index === activeTab;
                                            return (
                                                <button
                                                    key={tab.sys.id}
                                                    role="tab"
                                                    aria-selected={isActive}
                                                    aria-controls={`tabpanel-${tab.sys.id}`}
                                                    id={`tab-${tab.sys.id}`}
                                                    className={classNames(
                                                        'px-6 py-4 text-base font-bold font-headline tracking-wide whitespace-nowrap transition-colors cursor-pointer relative before:absolute before:bottom-0 before:left-0 before:right-0 before:h-0 before:transition-all before:duration-300 hover:before:h-1.5',
                                                        {
                                                            'bg-white before:bg-white text-gray-900':
                                                                isActive && isDark,
                                                            'bg-gray-900 before:bg-gray-900 text-white':
                                                                isActive && !isDark,
                                                            'text-white before:bg-white hover:text-white':
                                                                !isActive && isDark,
                                                            'text-gray-400 before:bg-gray-900 hover:text-gray-900':
                                                                !isActive && !isDark,
                                                        }
                                                    )}
                                                    onClick={() => setActiveTab(index)}
                                                >
                                                    {tab.fields.tabLabel}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                                {canScrollLeft && (
                                    <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-6 bg-linear-to-l from-transparent to-gray-900" />
                                )}
                                {canScrollRight && (
                                    <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-6 bg-linear-to-r from-transparent to-gray-900" />
                                )}
                            </div>

                            {activeTabItem?.fields && <TabPanel tabItem={activeTabItem} isDark={isDark} mode={mode} />}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

type TabPanelProps = {
    tabItem: TypeTabbedContentItem<'WITHOUT_UNRESOLVABLE_LINKS'>;
    isDark: boolean;
    mode: 'dark' | 'light';
};

const TabPanel = ({ tabItem, isDark, mode }: TabPanelProps) => {
    const updatedItem = useContentfulLiveUpdates(tabItem);
    const itemInspectorProps = useContentfulInspectorMode({ entryId: updatedItem.sys.id });
    const tabFields = updatedItem.fields;
    const imageCloudinary =
        Array.isArray(tabFields?.image) && tabFields.image.length > 0 ? (tabFields.image[0] as CloudinaryType) : null;

    return (
        <div
            role="tabpanel"
            id={`tabpanel-${updatedItem.sys.id}`}
            aria-labelledby={`tab-${updatedItem.sys.id}`}
            className="mt-8"
        >
            <div className="flex flex-col md:grid md:grid-cols-12 md:gap-4 md:items-center">
                <div className="col-span-5 space-y-6">
                    {tabFields.headerText && (
                        <h5
                            className={classNames('h-xl', { 'text-white': isDark })}
                            {...itemInspectorProps({ fieldId: 'headerText' })}
                        >
                            {tabFields.headerText}
                        </h5>
                    )}
                    {tabFields.paragraph && (
                        <TextMarkdown
                            className={classNames('text-xl', { 'text-white': isDark })}
                            text={tabFields.paragraph}
                            mode={mode}
                            {...itemInspectorProps({ fieldId: 'paragraph' })}
                        />
                    )}
                </div>
                {imageCloudinary && (
                    <div className="col-span-6 col-start-7 mt-8 md:mt-0">
                        <Suspense fallback={<div />}>
                            <div className="relative aspect-20/17">
                                <CldImage
                                    className="object-cover w-full h-full"
                                    src={imageCloudinary.public_id}
                                    alt=""
                                    width={1600}
                                    height={1600}
                                    sizes="(max-width: 767px) 100vw, (min-width: 768px) 50vw"
                                />
                            </div>
                        </Suspense>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TabbedContent;
